import { QueryClient } from '@tanstack/react-query';
import type { PersistQueryClientOptions } from '@tanstack/react-query-persist-client';
import { indexedDbPersister } from './persister';

/**
 * The app-wide, offline-first QueryClient.
 *
 * The three settings that make offline-first work:
 *  - `networkMode: 'offlineFirst'` — queries serve cache instead of erroring
 *    when there's no connectivity, and (the important part) MUTATIONS *pause*
 *    instead of failing when offline, so they can be queued and replayed later.
 *  - `staleTime: Infinity` — cached data is treated as fresh, so the UI shows it
 *    instantly and we refetch deliberately (pull-to-refresh / invalidation)
 *    rather than on every mount or window focus.
 *  - `cacheTime: Infinity` — never garbage-collect, so data survives for offline
 *    use and is available to be persisted to IndexedDB.
 *
 * Note: hooks that set their own `staleTime` keep it — that only affects
 * background refetch timing. Offline read behavior comes from `networkMode` +
 * persistence, not from `staleTime`, so those overrides are harmless.
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      networkMode: 'offlineFirst',
      staleTime: Infinity,
      cacheTime: Infinity,
      retry: 1,
      refetchOnWindowFocus: false,
    },
    mutations: {
      networkMode: 'offlineFirst',
      // Don't auto-retry mutations: many writes here aren't idempotent, and a
      // failed *replay* should surface to the user (onError) rather than risk a
      // duplicate write.
      retry: 0,
    },
  },
});

/**
 * Options for PersistQueryClientProvider — persists the cache (and the offline
 * mutation queue) to IndexedDB and restores it on launch.
 *
 * The v4 defaults already persist only successful queries and *paused*
 * mutations, which is exactly the offline write-queue we want — no
 * `dehydrateOptions` override needed.
 */
export const persistOptions: Omit<PersistQueryClientOptions, 'queryClient'> = {
  persister: indexedDbPersister,
  // Effective TTL for persisted data on restore. cacheTime is Infinity, so this
  // is the real upper bound on how stale offline data may be before we drop it.
  maxAge: 1000 * 60 * 60 * 24 * 7, // 7 days
  // Invalidate the persisted cache whenever the app version changes — a new
  // build may have changed query shapes, and reading an old shape would be a
  // subtle source of bugs. __APP_VERSION__ is injected by Vite.
  buster: __APP_VERSION__,
};
